import { Button, Col, Form, Input, Modal, Row, Select, Space, notification } from 'antd';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { LIST_DEFAULT_SHIFT, ROLE_USER, TYPE_DEPARTMENT } from 'constants/common';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { useDepartmentOptions } from 'hooks/use-department-options';
import VerifiedTick, {
  VERIFIED_BADGE_LABEL,
  VERIFIED_BADGE_MEANING,
  VerifiedBadge,
} from 'components/verified-tick';
import { MEMBER_GROUP_LABELS } from '../../membership-labels';
import MemberHeader from './member-header';
import SecuritySection from './security-section';
import ToggleRow from './toggle-row';
import {
  MemberFormValues,
  PHONE_CODES,
  USERNAME_RULES,
  buildPayload,
  hpStaffShown,
  initialValues,
  showOutstanding,
  showVip,
} from './form-values';
import styles from './member-modal.module.scss';

interface Props {
  isOpen: boolean;
  handleCancelCreateEditModal: () => void;
  onChangeStatusUpdateData: () => void;
  valueUserSelected: any;
}

const ROLE_OPTIONS = [
  { value: ROLE_USER.ADMIN, label: 'Admin' },
  { value: ROLE_USER.MOD, label: 'Mod' },
  { value: ROLE_USER.AGENCY, label: 'Agency' },
  { value: ROLE_USER.CREATOR, label: 'Online Creator' },
  { value: ROLE_USER.STUDIO_CREATOR, label: 'Studio Creator' },
  { value: ROLE_USER.USER, label: 'User' },
];

/** Dự phòng khi backend chưa có danh mục Bộ phận (API /departments). */
const DEPARTMENT_OPTIONS = [
  { value: TYPE_DEPARTMENT.HR, label: 'Nhân sự' },
  { value: TYPE_DEPARTMENT.TECHNICAL, label: 'Kỹ thuật' },
  { value: TYPE_DEPARTMENT.MAKEUP, label: 'Makeup' },
  { value: TYPE_DEPARTMENT.MC, label: 'MC' },
  { value: TYPE_DEPARTMENT.IDOL, label: 'Creator' },
  { value: TYPE_DEPARTMENT.CONTENT, label: 'Content' },
  { value: TYPE_DEPARTMENT.STYLE_LIST, label: 'Style List' },
  { value: TYPE_DEPARTMENT.DANCE_TEACHER, label: 'Dance Teacher' },
  { value: TYPE_DEPARTMENT.MANAGER, label: 'QL Vận Hành' },
];

const BADGE_OPTIONS = [
  { value: 'NONE', label: 'Không' },
  ...(['PINK', 'BLUE'] as VerifiedBadge[]).map((b) => ({
    value: b,
    label: (
      <span className={styles.badgeOption}>
        <VerifiedTick badge={b} size={14} /> {VERIFIED_BADGE_LABEL[b]}
      </span>
    ),
  })),
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

/** Popup Sửa / Thêm thành viên làm lại 30/09/2026 (docs/xac-minh-danh-tinh §5). */
const ModalCreateEditUser = ({
  isOpen,
  valueUserSelected,
  handleCancelCreateEditModal,
  onChangeStatusUpdateData,
}: Props) => {
  const [form] = Form.useForm<MemberFormValues>();
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState<any>(valueUserSelected);
  const isEdit = !!valueUserSelected?.id;
  const initial = useMemo(() => initialValues(valueUserSelected), [valueUserSelected]);
  const { options: departmentOptions, loading: departmentsLoading } = useDepartmentOptions(
    DEPARTMENT_OPTIONS,
    valueUserSelected?.department
  );

  const role = Form.useWatch('roles', form);
  const department = Form.useWatch('department', form);
  const badge = Form.useWatch('verifiedBadge', form);
  const nickName = Form.useWatch('nickName', form);
  const username = Form.useWatch('username', form);

  useEffect(() => {
    form.setFieldsValue(initial);
    setAccount(valueUserSelected);
  }, [form, initial, valueUserSelected]);

  const onValuesChange = (changed: Partial<MemberFormValues>) => {
    if ('department' in changed) form.setFieldValue('defaultShift', undefined);
    if (changed.roles) {
      if (changed.roles !== ROLE_USER.STUDIO_CREATOR) form.setFieldValue('studioCreatorBranch', undefined);
      if (changed.roles === ROLE_USER.CREATOR) {
        form.setFieldsValue({ department: undefined, defaultShift: undefined });
      }
    }
  };

  const onFinish = async (values: MemberFormValues) => {
    const payload = buildPayload(values, initial, isEdit);
    if (isEdit && Object.keys(payload).length === 0) {
      handleCancelCreateEditModal();
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await AxiosUtils.put(`${API.editUser}/${valueUserSelected.id}`, payload);
      else await AxiosUtils.post(API.createUser, payload);
      notification.success({ message: isEdit ? 'Đã lưu thay đổi' : 'Đã thêm thành viên', duration: 3 });
      handleCancelCreateEditModal();
      onChangeStatusUpdateData();
    } catch (error: any) {
      const message = Array.isArray(error?.message) ? error.message.join(', ') : error?.message;
      notification.error({
        message: 'Không lưu được',
        description: message || 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 6,
      });
    } finally {
      setSaving(false);
    }
  };

  const shifts = LIST_DEFAULT_SHIFT.filter((s) => s.department === department);
  const showShift = [TYPE_DEPARTMENT.HR, TYPE_DEPARTMENT.IDOL].includes(department as TYPE_DEPARTMENT);
  const perks = showOutstanding(role) || showVip(role);
  const hpStaff = hpStaffShown(valueUserSelected, role, department);

  return (
    <Modal
      open={isOpen}
      title={isEdit ? 'Sửa thành viên' : 'Thêm thành viên'}
      onCancel={handleCancelCreateEditModal}
      width={760}
      centered
      className={styles.modal}
      footer={
        <Space>
          <Button onClick={handleCancelCreateEditModal}>Hủy</Button>
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            {isEdit ? 'Lưu thay đổi' : 'Thêm thành viên'}
          </Button>
        </Space>
      }
    >
      {isEdit ? (
        <MemberHeader
          id={valueUserSelected.id}
          nickName={nickName ?? ''}
          username={username ?? ''}
          avatar={valueUserSelected?.profile?.avatar}
          role={role}
          badge={badge && badge !== 'NONE' ? badge : null}
          verified={!!account?.isVerifyAccount}
          locked={!!account?.isAccountDisabled}
          hpStaff={hpStaff}
        />
      ) : null}

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        autoComplete="off"
        className={styles.form}
      >
        <Section title="Vai trò & bộ phận">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="roles" label="Vai trò" rules={[{ required: true, message: 'Chọn vai trò' }]}>
                <Select placeholder="Chọn vai trò" options={ROLE_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="department" label="Bộ phận">
                <Select
                  placeholder="Không thuộc bộ phận nào"
                  allowClear
                  loading={departmentsLoading}
                  options={departmentOptions}
                  disabled={role === ROLE_USER.CREATOR}
                />
              </Form.Item>
            </Col>
            {role === ROLE_USER.STUDIO_CREATOR ? (
              <Col xs={24} md={12}>
                <Form.Item
                  name="studioCreatorBranch"
                  label="Nhánh Studio Creator"
                  rules={[{ required: true, message: 'Chọn nhánh' }]}
                >
                  <Select
                    placeholder="Chọn nhánh"
                    options={[
                      { value: 'DANCE', label: 'Dance Creator' },
                      { value: 'NPC', label: 'NPC Creator' },
                    ]}
                  />
                </Form.Item>
              </Col>
            ) : null}
            {showShift ? (
              <Col xs={24} md={12}>
                <Form.Item name="defaultShift" label="Ca làm việc" rules={[{ required: true, message: 'Chọn ca' }]}>
                  <Select
                    placeholder="Chọn ca"
                    options={shifts.map((s) => ({ value: s.id.toString(), label: s.name }))}
                  />
                </Form.Item>
              </Col>
            ) : null}
          </Row>
          <p className={styles.hint}>
            Nhân viên HP: <b>{hpStaff ? 'Có' : 'Không'}</b> — tự tính lại khi đổi vai trò hoặc Bộ phận.
            {role === ROLE_USER.STUDIO_CREATOR ? ' Studio Creator tham gia Điểm SAO; nhánh quyết định nhiệm vụ.' : ''}
            {role === ROLE_USER.CREATOR ? ' Online Creator chỉ xem data và BXH, không tham gia Điểm SAO.' : ''}
          </p>
          {valueUserSelected?.requestedMemberGroup &&
          valueUserSelected?.roles === ROLE_USER.USER &&
          valueUserSelected.requestedMemberGroup !== 'USER' ? (
            <p className={styles.hint}>
              Đã đăng ký: {MEMBER_GROUP_LABELS[valueUserSelected.requestedMemberGroup]}. Xác nhận ngay tại danh
              sách thành viên.
            </p>
          ) : null}
        </Section>

        <Section title="Tài khoản & ID">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="username"
                label="TikTok ID (username)"
                validateFirst
                rules={USERNAME_RULES}
              >
                <Input placeholder="Nhập TikTok ID" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="tiktokUid"
                label="UID TikTok"
                tooltip="Cột B “ID nhà sáng tạo” trong file TikTok. Để trống để gỡ liên kết."
                rules={[
                  {
                    validator: (_, v) => {
                      const d = String(v ?? '').replace(/\s/g, '');
                      return !d || /^\d{5,32}$/.test(d)
                        ? Promise.resolve()
                        : Promise.reject(new Error('UID chỉ gồm chữ số (thường 19 số)'));
                    },
                  },
                ]}
              >
                <Input inputMode="numeric" placeholder="VD: 7381702185756770321" allowClear className={styles.mono} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="verifiedBadge"
                label="Xác minh danh tính"
                extra={
                  badge && badge !== 'NONE'
                    ? VERIFIED_BADGE_MEANING[badge as VerifiedBadge]
                    : 'Hồng: tài khoản HP Media · Xanh: đã xác minh'
                }
              >
                <Select options={BADGE_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
        </Section>

        <Section title="Liên hệ">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="nickName"
                label="Tên hiển thị"
                rules={[
                  { required: true, message: 'Nhập tên hiển thị' },
                  { max: 25, message: 'Không được quá 25 kí tự' },
                ]}
              >
                <Input placeholder="Nhập tên hiển thị" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
                validateFirst
                rules={[
                  { required: true, message: 'Nhập email' },
                  { type: 'email', message: 'Vui lòng nhập đúng định dạng email' },
                  { max: 255, message: 'Email không được quá 255 kí tự' },
                ]}
              >
                <Input placeholder="Nhập email" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Số điện thoại">
                <Space.Compact block>
                  <Form.Item name="phoneCode" noStyle>
                    <Select
                      className={styles.phoneCode}
                      options={PHONE_CODES.map((c) => ({ value: c, label: `+${c}` }))}
                    />
                  </Form.Item>
                  <Form.Item name="phoneNumber" noStyle>
                    <Input placeholder="Nhập SĐT" allowClear inputMode="tel" />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="about"
                label="Data cá nhân"
                tooltip="Có liên kết thì thành viên thấy mục Data cá nhân trong TÔI; để trống để ẩn."
                rules={[
                  {
                    validator: (_, value) => {
                      const link = value?.trim();
                      if (!link || link === (valueUserSelected?.about ?? '').trim()) return Promise.resolve();
                      try {
                        const url = new URL(link);
                        if (url.protocol === 'http:' || url.protocol === 'https:') return Promise.resolve();
                      } catch {
                        // không phải liên kết
                      }
                      return Promise.reject(new Error('Nhập liên kết http(s) hợp lệ'));
                    },
                  },
                ]}
              >
                <Input placeholder="https://..." allowClear />
              </Form.Item>
            </Col>
          </Row>
        </Section>

        {perks ? (
          <Section title="Hiển thị & quyền lợi">
            {showOutstanding(role) ? (
              <ToggleRow
                name="isOutStanding"
                label="Hiện ở mục nổi bật Trang chủ"
                hint={
                  role === ROLE_USER.AGENCY
                    ? 'Dải Đối tác nổi bật và trang Agency trong app.'
                    : 'Dải Creator nổi bật và trang Creator trong app.'
                }
              />
            ) : null}
            {showVip(role) ? (
              <ToggleRow
                name="isVip"
                label="Cho dùng thư viện Âm thanh (VIP)"
                hint="Tài khoản User thường mặc định không dùng được Âm thanh."
              />
            ) : null}
          </Section>
        ) : null}

        {isEdit ? (
          <Section title="Bảo mật">
            <SecuritySection
              account={account ?? {}}
              onChanged={(patch) => {
                setAccount((prev: any) => ({ ...prev, ...patch }));
                onChangeStatusUpdateData();
              }}
            />
          </Section>
        ) : (
          <Section title="Mật khẩu">
            <Form.Item
              name="password"
              label="Mật khẩu"
              rules={[
                { required: true, message: 'Trường này là bắt buộc.' },
                { min: 8, message: 'Mật khẩu cần ít nhất 8 kí tự' },
                { max: 15, message: 'Mật khẩu cần ít hơn 16 kí tự' },
              ]}
            >
              <Input.Password autoComplete="new-password" placeholder="8–15 kí tự" />
            </Form.Item>
          </Section>
        )}
      </Form>
    </Modal>
  );
};

export default ModalCreateEditUser;
